import * as React from 'react';
import { Lock, Tag, BookmarkCheck } from 'lucide-react';
import type { TFunction } from 'i18next';
import type { CellEntry } from '@ooh/contracts';
import type { CellState } from '../lib/availability-types';

const HATCH_BG =
  'repeating-linear-gradient(-45deg, transparent, transparent 4px, rgba(0,0,0,0.11) 4px, rgba(0,0,0,0.11) 5px)';
/** Lighter stripe for the dark hard-reservation fill — HATCH_BG's black stripe is
 * nearly invisible against `--status-hard`'s deep-navy background. Uses the
 * `--hatch-stripe-on-dark` DS token (packages/ui/src/styles/globals.css) instead of a raw
 * rgba() literal. */
const HATCH_BG_ON_DARK =
  'repeating-linear-gradient(-45deg, transparent, transparent 4px, var(--hatch-stripe-on-dark) 4px, var(--hatch-stripe-on-dark) 5px)';

/**
 * Variant B palette — see `apps/mockups/src/data/availability-status-colors.ts:120-142`,
 * the client-approved source of truth. Every status is a distinct, design-token-derived
 * fill; HARD_RESERVATION uses the dark `--status-hard` token with light text.
 */
const CELL_COLOR_CLASSES: Record<CellState, string> = {
  free: 'bg-status-free/10 border-status-free/40',
  'option-own': 'bg-status-option/20 border-status-option',
  'option-other': 'bg-status-option/20 border-status-option',
  'reserved-own-soft': 'bg-status-reserved/25 border-status-reserved',
  'reserved-other-soft': 'bg-status-reserved/25 border-status-reserved',
  'reserved-own-hard': 'bg-status-hard border-status-hard-border text-white',
  'reserved-other-hard': 'bg-status-hard border-status-hard-border text-white',
  sold: 'bg-status-sold/25 border-status-sold',
  unknown: 'bg-zinc-100 border-zinc-300',
  unavailable: 'bg-status-unavailable border-zinc-300',
};

export function getCellColorClasses(state: CellState): string {
  return CELL_COLOR_CLASSES[state];
}

/**
 * The `common.json` `status.*` keys `CELL_STATE_LABEL_KEYS`/`ENTRY_STATUS_LABEL_KEYS` map
 * to — a literal union rather than `string` so `tCommon(CELL_STATE_LABEL_KEYS[state])`
 * still typechecks against `TFunction<'common'>`'s literal key overloads.
 */
export type CommonStatusLabelKey =
  | 'status.free'
  | 'status.unknown'
  | 'status.option'
  | 'status.reservedSoft'
  | 'status.reservedHard'
  | 'status.sold'
  | 'status.unavailable';

/**
 * Single source of truth for the `common.json` `status.*` translation key backing each
 * `CellState` — `buildAvailabilityLegendItems` (inventory-availability-shared.tsx) and
 * `buildEntryShortLabel`/`buildEntryFullLabel` below both read from this map instead of
 * each maintaining their own key literals, so a cell's in-grid label, its popover label,
 * and its legend entry can never drift apart (CU-86cbh0jhy — a base-state entry with no
 * clientName/holderName used to fall back to the raw, untranslated status enum).
 */
export const CELL_STATE_LABEL_KEYS: Record<CellState, CommonStatusLabelKey> = {
  free: 'status.free',
  unknown: 'status.unknown',
  'option-own': 'status.option',
  'option-other': 'status.option',
  'reserved-own-soft': 'status.reservedSoft',
  'reserved-other-soft': 'status.reservedSoft',
  'reserved-own-hard': 'status.reservedHard',
  'reserved-other-hard': 'status.reservedHard',
  sold: 'status.sold',
  unavailable: 'status.unavailable',
};

export function getCellStateLabel(state: CellState, tCommon: TFunction<'common'>): string {
  return tCommon(CELL_STATE_LABEL_KEYS[state]);
}

/**
 * Maps a raw `CellEntry.status` to its `status.*` label, reusing `CELL_STATE_LABEL_KEYS`
 * directly rather than re-deriving the entry's ownership-aware `CellState` (that
 * derivation, `cellEntryToCellState`, lives in `availability-shared/availability-tab-grid.ts`
 * and already imports from this file — importing it back here would cycle). The label
 * itself never depends on ownership (`option-own`/`option-other` both read "Opcja"), so a
 * fixed representative `CellState` per raw status is sufficient.
 */
export const ENTRY_STATUS_LABEL_KEYS: Record<CellEntry['status'], CommonStatusLabelKey> = {
  FREE: CELL_STATE_LABEL_KEYS.free,
  ASK_AVAILABILITY: CELL_STATE_LABEL_KEYS.unknown,
  POTENTIAL: CELL_STATE_LABEL_KEYS.unknown,
  OPTION: CELL_STATE_LABEL_KEYS['option-other'],
  SOFT_RESERVATION: CELL_STATE_LABEL_KEYS['reserved-other-soft'],
  HARD_RESERVATION: CELL_STATE_LABEL_KEYS['reserved-other-hard'],
  SOLD: CELL_STATE_LABEL_KEYS.sold,
  UNAVAILABLE: CELL_STATE_LABEL_KEYS.unavailable,
};

function getCellCursorClass(state: CellState, isClickable: boolean): string {
  if (state === 'sold' || state === 'unavailable') return 'cursor-not-allowed';
  if (
    state === 'option-other' ||
    state === 'reserved-other-soft' ||
    state === 'reserved-other-hard'
  )
    return 'cursor-default';
  return isClickable ? 'cursor-pointer' : 'cursor-default';
}

function getFreeHoverClass(state: CellState, isClickable: boolean): string {
  return state === 'free' && isClickable ? 'hover:bg-green-50' : '';
}

export function getCellTdClasses(
  state: CellState,
  isSelected: boolean,
  selectedClassName = 'ring-2 ring-inset ring-primary z-10',
  isClickable = true,
  /**
   * When true, the cell's sole entry is sourced from an availability_windows row
   * (fromAvailabilityWindow=true) — renders a dashed border in addition to the
   * status color, distinguishing it from a base-state cell of the same status
   * (e.g. a FREE window cell vs. a plain FREE base-state cell).
   */
  isWindowSourced = false,
): string {
  const base = 'border transition-colors relative';
  const sel = isSelected ? selectedClassName : '';
  const colors = getCellColorClasses(state);
  const cursor = getCellCursorClass(state, isClickable);
  const hover = getFreeHoverClass(state, isClickable);
  // A thin (1px) dashed border is nearly imperceptible in a compact calendar grid —
  // border-2 keeps the dash segments long enough to read as a distinct treatment.
  const windowBorder = isWindowSourced ? 'border-2 border-dashed' : '';
  return [base, colors, hover, cursor, windowBorder, sel].filter(Boolean).join(' ');
}

export function getHatchStyle(state: CellState): React.CSSProperties {
  if (state === 'reserved-other-hard') return { backgroundImage: HATCH_BG_ON_DARK };
  if (state === 'option-other' || state === 'reserved-other-soft') {
    return { backgroundImage: HATCH_BG };
  }
  return {};
}

const OPTION_STATES = new Set<CellState>(['option-own', 'option-other']);
const RESERVED_STATES = new Set<CellState>([
  'reserved-own-soft',
  'reserved-other-soft',
  'reserved-own-hard',
  'reserved-other-hard',
]);
const HARD_RESERVED_STATES = new Set<CellState>(['reserved-own-hard', 'reserved-other-hard']);

/** Renders `Icon` with an accessible label when provided, else hidden from the a11y tree. */
function StatusIcon({
  icon: Icon,
  className,
  ariaLabel,
  testId,
}: {
  icon: typeof Lock;
  className: string;
  ariaLabel?: string;
  testId?: string;
}) {
  return ariaLabel ? (
    <Icon className={className} aria-label={ariaLabel} data-testid={testId} />
  ) : (
    <Icon className={className} aria-hidden="true" data-testid={testId} />
  );
}

export function CellStateIcon({
  state,
  soldAriaLabel,
  soldTestId,
  optionAriaLabel,
  reservedAriaLabel,
}: {
  state: CellState;
  soldAriaLabel?: string;
  soldTestId?: string;
  /** Accessible label for the OPTION-bucket status icon (option-own/option-other). */
  optionAriaLabel?: string;
  /**
   * Accessible label for the shared reserved-bucket status icon (reserved-own-soft/
   * reserved-own-hard/reserved-other-soft/reserved-other-hard) — covers SOFT_RESERVATION
   * and HARD_RESERVATION, own and other, which all share one canonical icon regardless of
   * the split fill color.
   */
  reservedAriaLabel?: string;
}) {
  if (state === 'sold') {
    return (
      <StatusIcon
        icon={Lock}
        className="h-3 w-3 text-zinc-400"
        ariaLabel={soldAriaLabel}
        testId={soldTestId}
      />
    );
  }

  if (OPTION_STATES.has(state)) {
    return <StatusIcon icon={Tag} className="h-3 w-3 text-zinc-500" ariaLabel={optionAriaLabel} />;
  }

  if (RESERVED_STATES.has(state)) {
    // Light color on the dark hard fill for visibility; unchanged gray on the light soft fill.
    const colorClass = HARD_RESERVED_STATES.has(state) ? 'text-white' : 'text-zinc-500';
    return (
      <StatusIcon
        icon={BookmarkCheck}
        className={`h-3 w-3 ${colorClass}`}
        ariaLabel={reservedAriaLabel}
      />
    );
  }

  if (state === 'unknown') {
    return <span className="text-2xs font-semibold text-zinc-400">?</span>;
  }

  return null;
}
